import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBrandStagesDrillCache,
  setBrandStagesAllFunnelData,
  setBrandStagesAllFunnelError,
  setBrandStagesAllFunnelLoading,
  setBrandStagesBrandFunnelsData,
  setBrandStagesBrandsData,
  setBrandStagesDrillCacheEntry,
  setBrandStagesMatrixError,
  setBrandStagesMatrixLoading,
} from "../../../../../features/optifundDashboardSlice";
import { setDisplayErrorSnackBar } from "../../../../../features/userSlice";
import { OptifundDashboardEndPointLogic } from "../../../../../logic/AssetManagement/overview/OptifundDashboard/OptifundDashboardEndPointLogic";
import { OptifundDashboardQueryIds } from "../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import {
  ALL_BRANDS_UID,
  BRAND_STAGES,
  BrandStagesRange,
  drillCacheKey,
  mapBrandsFromStatusRows,
} from "../BrandStages/brandStagesApiMapping";

const REFRESHED_MESSAGE = "Brand stages refreshed for the selected range";

function leadFunnelParams(range: BrandStagesRange, brandUid: string): Record<string, string> {
  return { start_date: range.start, end_date: range.end, brand_uid: brandUid };
}

function brandStatusParams(range: BrandStagesRange, brandUid: string, state: string): Record<string, string> {
  return { start_date: range.start, end_date: range.end, brand_uid: brandUid, state };
}

export interface UseBrandStagesDataResult {
  retryAllFunnel: () => void;
  retryMatrix: () => void;
}

// Runs every chart query for the Brand Stages tab, blocking until
// `websiteSharedUid` is resolved (spec Section 4). Two independent chains
// run in parallel:
//   1. Lead Funnel for all brands (brand_uid '0') -> KPI cards + funnel.
//   2. Brand Status Query (all brands, Paid) -> the brand list, then one
//      Lead Funnel call per brand_uid, all in parallel -> the matrix. The
//      Paid rows also seed the drill-down cache for (all brands, Paid).
// Re-runs whenever the applied range or `fetchToken` changes; a non-zero
// fetchToken means GET DATA / Refresh triggered it, which clears the
// drill-down cache and shows the success toast once both chains succeed.
// A generation counter drops responses from superseded runs.
export function useBrandStagesData(
  userToken: any,
  websiteSharedUid: string | null,
  range: BrandStagesRange,
  fetchToken: number
): UseBrandStagesDataResult {
  const dispatch = useDispatch();
  const generationRef = useRef(0);

  const loadAllFunnel = useCallback(
    async (generation: number): Promise<boolean> => {
      dispatch(setBrandStagesAllFunnelLoading(true));
      dispatch(setBrandStagesAllFunnelError(false));
      const rows = await OptifundDashboardEndPointLogic.runQueryWithParams(
        userToken,
        OptifundDashboardQueryIds.brandStagesLeadFunnelId,
        leadFunnelParams(range, ALL_BRANDS_UID)
      );
      if (generation !== generationRef.current) return false;
      if (rows) dispatch(setBrandStagesAllFunnelData(rows));
      dispatch(setBrandStagesAllFunnelError(!rows));
      dispatch(setBrandStagesAllFunnelLoading(false));
      return !!rows;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userToken, range.start, range.end, dispatch]
  );

  const loadMatrix = useCallback(
    async (generation: number): Promise<boolean> => {
      dispatch(setBrandStagesMatrixLoading(true));
      dispatch(setBrandStagesMatrixError(false));
      const paidRows = await OptifundDashboardEndPointLogic.runQueryWithParams(
        userToken,
        OptifundDashboardQueryIds.brandStagesStatusId,
        brandStatusParams(range, ALL_BRANDS_UID, BRAND_STAGES[0].state)
      );
      if (generation !== generationRef.current) return false;
      if (!paidRows) {
        dispatch(setBrandStagesBrandsData(null));
        dispatch(setBrandStagesBrandFunnelsData({}));
        dispatch(setBrandStagesMatrixError(true));
        dispatch(setBrandStagesMatrixLoading(false));
        return false;
      }
      dispatch(
        setBrandStagesDrillCacheEntry({
          key: drillCacheKey(ALL_BRANDS_UID, BRAND_STAGES[0].state, range),
          rows: paidRows,
        })
      );

      const brands = mapBrandsFromStatusRows(paidRows);
      const results = await Promise.all(
        brands.map((brand) =>
          OptifundDashboardEndPointLogic.runQueryWithParams(
            userToken,
            OptifundDashboardQueryIds.brandStagesLeadFunnelId,
            leadFunnelParams(range, brand.brandUid)
          )
        )
      );
      if (generation !== generationRef.current) return false;

      const funnelsByBrand: Record<string, any> = {};
      let anyFailed = false;
      results.forEach((rows, index) => {
        if (rows) funnelsByBrand[brands[index].brandUid] = rows;
        else anyFailed = true;
      });
      dispatch(setBrandStagesBrandsData(brands));
      dispatch(setBrandStagesBrandFunnelsData(funnelsByBrand));
      dispatch(setBrandStagesMatrixError(anyFailed));
      dispatch(setBrandStagesMatrixLoading(false));
      return !anyFailed;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userToken, range.start, range.end, dispatch]
  );

  useEffect(() => {
    if (!websiteSharedUid) {
      return;
    }

    const generation = ++generationRef.current;
    if (fetchToken > 0) dispatch(clearBrandStagesDrillCache());
    Promise.all([loadAllFunnel(generation), loadMatrix(generation)]).then(([allOk, matrixOk]) => {
      if (generation === generationRef.current && fetchToken > 0 && allOk && matrixOk) {
        dispatch(setDisplayErrorSnackBar({ message: REFRESHED_MESSAGE, type: "success" }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteSharedUid, range.start, range.end, fetchToken]);

  const retryAllFunnel = useCallback(() => {
    loadAllFunnel(generationRef.current);
  }, [loadAllFunnel]);

  const retryMatrix = useCallback(() => {
    loadMatrix(generationRef.current);
  }, [loadMatrix]);

  return { retryAllFunnel, retryMatrix };
}

export interface BrandStagesDrill {
  brandUid: string;
  brandName: string | null;
  stageIndex: number;
}

export interface UseBrandStagesDrillDownResult {
  rows: any[] | null;
  isError: boolean;
  retry: () => void;
}

// One Brand Status Query request per opened drill-down, cached in Redux by
// (brand_uid, state, range) - reopening the same stage x brand reuses it.
// The cache is cleared by GET DATA / Refresh (see useBrandStagesData).
export function useBrandStagesDrillDown(
  userToken: any,
  websiteSharedUid: string | null,
  range: BrandStagesRange,
  drill: BrandStagesDrill | null
): UseBrandStagesDrillDownResult {
  const dispatch = useDispatch();
  const drillCache = useSelector((state: any) => state.optifundDashboard.brandStagesDrillCache);
  const [isError, setIsError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const state = drill ? BRAND_STAGES[drill.stageIndex].state : null;
  const key = drill && state ? drillCacheKey(drill.brandUid, state, range) : null;
  const cachedRows: any[] | undefined = key ? drillCache[key] : undefined;

  const cachedRowsRef = useRef(cachedRows);
  cachedRowsRef.current = cachedRows;

  useEffect(() => {
    setIsError(false);
    if (!key || !drill || !state || !websiteSharedUid || cachedRowsRef.current) {
      return;
    }

    let cancelled = false;
    OptifundDashboardEndPointLogic.runQueryWithParams(
      userToken,
      OptifundDashboardQueryIds.brandStagesStatusId,
      brandStatusParams(range, drill.brandUid, state)
    ).then((rows) => {
      if (cancelled) return;
      if (rows) dispatch(setBrandStagesDrillCacheEntry({ key, rows }));
      else setIsError(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, websiteSharedUid, retryToken]);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  return { rows: cachedRows ?? null, isError, retry };
}
